/**
 * Wizard draft autosave — JSON snapshots of the New Site wizard, scoped per uid.
 *
 * Lives entirely client-side in localStorage. Storage holds lightweight metadata
 * only: PlaceResult internals collapse to lat/lng + name. The wizard no longer
 * collects documents — those move to the SiteLandingPage Documents tab — so
 * `uploadedDocs` is gone from the snapshot.
 *
 * The wizard collapsed from 5 steps to 3 (Name → Boundary → Brief). Drafts
 * with `wizardStep > 3` from older builds are clamped to 3 on load, and the
 * project-intent fields they captured carry forward into the Brief step.
 */

import type { ProjectIntent } from '../types/siteRag';
import type { BoundaryResult } from '../components/siterag/SiteBoundaryDrawer';
import type { SiteLayer } from '../types/site';

export type WizardStep = 1 | 2 | 3;

/** Subset of `google.maps.places.PlaceResult` we actually consume downstream. */
export interface PersistedPlace {
  name?: string;
  formatted_address?: string;
  lat: number;
  lng: number;
}

export interface WizardDraftSnapshot {
  draftId: string;
  wizardStep: WizardStep;
  siteName: string;
  selectedPlace: PersistedPlace | null;
  boundaryResult: BoundaryResult | null;
  boundarySkipped: boolean;
  projectIntent: ProjectIntent;
  /** Free-form site description captured on the Brief step. */
  siteDescription?: string;
  /** Layers committed via the LayerReviewPanel (KMZ import). Empty when the
   *  user drew the boundary or skipped the panel. */
  parsedSiteLayers?: SiteLayer[];
  savedAt: number;
}

const KEY_PREFIX = 'da-site-draft';

export const draftKey = (uid: string, draftId: string): string =>
  `${KEY_PREFIX}:${uid}:${draftId}`;

export const draftKeyPrefix = (uid: string): string => `${KEY_PREFIX}:${uid}:`;

export function newDraftId(now: number = Date.now()): string {
  return `draft-${now}`;
}

export interface SnapshotInput {
  draftId: string;
  wizardStep: WizardStep;
  siteName: string;
  selectedPlace: google.maps.places.PlaceResult | null;
  boundaryResult: BoundaryResult | null;
  boundarySkipped: boolean;
  projectIntent: ProjectIntent;
  siteDescription?: string;
  parsedSiteLayers?: ReadonlyArray<SiteLayer>;
  now?: number;
}

/** Build the JSON-safe snapshot. Strips File-bound and PlaceResult-internal fields. */
export function buildSnapshot(input: SnapshotInput): WizardDraftSnapshot {
  return {
    draftId: input.draftId,
    wizardStep: input.wizardStep,
    siteName: input.siteName,
    selectedPlace: persistPlace(input.selectedPlace),
    boundaryResult: input.boundaryResult,
    boundarySkipped: input.boundarySkipped,
    projectIntent: input.projectIntent,
    siteDescription: input.siteDescription ?? '',
    parsedSiteLayers: input.parsedSiteLayers ? input.parsedSiteLayers.map(l => ({ ...l })) : [],
    savedAt: input.now ?? Date.now(),
  };
}

function persistPlace(place: google.maps.places.PlaceResult | null): PersistedPlace | null {
  if (!place || !place.geometry?.location) return null;
  const loc = place.geometry.location as unknown as { lat: number | (() => number); lng: number | (() => number) };
  const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    name: place.name,
    formatted_address: place.formatted_address,
    lat,
    lng,
  };
}

/** Reconstruct a PlaceResult-shaped object whose `.lat()` / `.lng()` work. */
export function placeFromPersisted(p: PersistedPlace | null): google.maps.places.PlaceResult | null {
  if (!p) return null;
  return {
    name: p.name,
    formatted_address: p.formatted_address,
    geometry: {
      location: {
        lat: () => p.lat,
        lng: () => p.lng,
      } as unknown as google.maps.LatLng,
    } as google.maps.places.PlaceGeometry,
  } as google.maps.places.PlaceResult;
}

export function saveDraft(uid: string, snapshot: WizardDraftSnapshot, storage: Storage = localStorage): void {
  storage.setItem(draftKey(uid, snapshot.draftId), JSON.stringify(snapshot));
}

export function removeDraft(uid: string, draftId: string, storage: Storage = localStorage): void {
  storage.removeItem(draftKey(uid, draftId));
}

/** Clamp a wizardStep value from a possibly-old draft (1..5) to the new range (1..3). */
export function clampWizardStep(raw: number | undefined): WizardStep {
  if (raw == null || !Number.isFinite(raw) || raw < 1) return 1;
  if (raw >= 3) return 3;
  return raw === 2 ? 2 : 1;
}

/**
 * Reads every draft for a uid, newest first. Skips entries that fail to parse.
 * Tolerates the old 5-step schema: `wizardStep` is clamped to 1..3, and any
 * legacy `uploadedDocs` field is ignored (the wizard no longer collects docs).
 */
export function loadDrafts(uid: string, storage: Storage = localStorage): WizardDraftSnapshot[] {
  const prefix = draftKeyPrefix(uid);
  const drafts: WizardDraftSnapshot[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(prefix)) continue;
    const raw = storage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as WizardDraftSnapshot & { wizardStep: number };
      if (parsed && typeof parsed.draftId === 'string' && typeof parsed.wizardStep === 'number') {
        parsed.wizardStep = clampWizardStep(parsed.wizardStep);
        if (typeof parsed.siteDescription !== 'string') parsed.siteDescription = '';
        drafts.push(parsed);
      }
    } catch {
      // Corrupt entry — leave it, the next save will overwrite.
    }
  }
  return drafts.sort((a, b) => b.savedAt - a.savedAt);
}

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Name',
  2: 'Boundary',
  3: 'Brief',
};

export function stepLabel(step: WizardStep): string {
  return STEP_LABELS[step];
}

export function describeDraft(snapshot: WizardDraftSnapshot, now: number = Date.now()): string {
  const label = stepLabel(snapshot.wizardStep);
  return `Step ${snapshot.wizardStep} — ${label}, last saved ${formatRelative(snapshot.savedAt, now)}`;
}

export function formatRelative(savedAt: number, now: number = Date.now()): string {
  const diffMs = Math.max(0, now - savedAt);
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** True if any wizard slice has progress beyond the fresh-open defaults. */
export function hasWizardProgress(input: {
  wizardStep: WizardStep;
  siteName: string;
  selectedPlace: unknown;
  boundaryResult: unknown;
  siteDescription?: string;
}): boolean {
  return (
    input.wizardStep > 1 ||
    input.siteName.trim() !== '' ||
    input.selectedPlace !== null ||
    input.boundaryResult !== null ||
    (input.siteDescription ?? '').trim() !== ''
  );
}
